import { useState } from "react";
import { useHistory, useRouteMatch } from "react-router";

import {
    BdsIconCopy,
    BdsIconPlus,
    BdsIconPlusCircle,
    BdsIconTemplate,
} from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { isRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation";
import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    type IHotkeyWithPopoverConfig,
    type THotkeyCommands,
} from "commonComponents/utilities/Hotkey/hotkey.types";

import "./NewJobPrompt.less";

export interface INewJobPromptProps {
    jobRoute: string | IRouteLocationState;
    templateRoute: string | IRouteLocationState;
    newName: string;
    block?: boolean;
    hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;
    onRecommendedTemplatesClick: () => Promise<void>;
    isTemplateMode?: boolean;
    forceModalOpen?: boolean;
    hideButton?: boolean;
    onModalClose?: () => void;
    onSubmit?: () => void;
}

interface INewJobPromptDisplayProps {
    newName: string;
    showModal: boolean;
    setShowModal: (value: React.SetStateAction<boolean>) => void;
    handleCreateFromScratch: () => void;
    handleCreateFromTemplate: () => void;
    handleRecommendedTemplatesClick: () => Promise<void>;
    canViewRecommendedTemplates?: boolean;
    onModalClose?: () => void;
}

export function NewJobPromptDisplay(props: INewJobPromptDisplayProps) {
    const {
        showModal,
        setShowModal,
        handleCreateFromScratch,
        handleCreateFromTemplate,
        handleRecommendedTemplatesClick,
        canViewRecommendedTemplates,
        onModalClose,
    } = props;

    const closeNewJobPromtModal = () => {
        setShowModal(false);
        onModalClose?.();
    };

    const isWebforms = window.location.pathname.includes("aspx");
    const isWebview = useWebviewContext() !== undefined;

    return (
        <BTModal
            beforeClose={closeNewJobPromtModal}
            data-testid="btModalIframeModal"
            visible={showModal}
            title={`New ${props.newName}`}
            centered
            width="640px"
            className="new-job-prompt"
        >
            <div className="new-job-prompt--subtitle">
                How would you like to set up your new {props.newName}?
            </div>
            <div className="new-job-prompt--buttons">
                {isWebforms ? (
                    <BTLink to={jobDetailRoute.link({ jobId: 0 }, { openCondensed: true })}>
                        <BTButton
                            data-testid="newJobFromScratch"
                            type="secondary"
                            icon={<BdsIconPlusCircle size="lg" />}
                        >
                            From scratch
                        </BTButton>
                    </BTLink>
                ) : (
                    <BTButton
                        data-testid="newJobFromScratch"
                        type="secondary"
                        icon={<BdsIconPlusCircle size="lg" />}
                        onClick={handleCreateFromScratch}
                    >
                        From scratch
                    </BTButton>
                )}

                <BTButton
                    data-testid="newJobFromTemplate"
                    type="secondary"
                    icon={<BdsIconCopy size="lg" />}
                    onClick={handleCreateFromTemplate}
                >
                    Your templates
                </BTButton>

                {/* Hide recommended templates in a webview for now because it routes you to the templates area */}
                {canViewRecommendedTemplates && !isWebview && (
                    <BTButton
                        data-testid="browseRecommendedTemplates"
                        onClick={handleRecommendedTemplatesClick}
                        type="secondary"
                        icon={<BdsIconTemplate size="lg" />}
                    >
                        Recommended templates
                    </BTButton>
                )}
            </div>
        </BTModal>
    );
}

export function NewJobPrompt(props: INewJobPromptProps) {
    const {
        newName,
        block,
        hotkey,
        onRecommendedTemplatesClick,
        isTemplateMode,
        onModalClose,
        hideButton,
        onSubmit,
    } = props;
    const jobRoute = isRouteLocationState(props.jobRoute)
        ? props.jobRoute.pathname
        : props.jobRoute;
    const templateRoute = isRouteLocationState(props.templateRoute)
        ? props.templateRoute.pathname
        : props.templateRoute;
    const history = useHistory();
    const match = useRouteMatch();

    const builderInfoContext = useRequiredContext(BuilderInfoContext);

    const [showModal, setShowModal] = useState(false);

    const handleCreateJobFromScratch = () => {
        setShowModal(false);
        history.push(jobRoute);
        onSubmit?.();
    };

    const handleCreateTemplateFromScratch = () => {
        setShowModal(false);
        history.push(`${match.url}${jobRoute}`);
        onSubmit?.();
    };

    const handleCreateFromTemplate = () => {
        setShowModal(false);
        history.push(`${match.url}${templateRoute}`);
        onSubmit?.();
    };

    const handleRecommendedTemplatesClick = async () => {
        await onRecommendedTemplatesClick();
        onSubmit?.();
    };

    const canViewRecommendedTemplates = builderInfoContext.canUseExternalTemplates;

    return (
        <>
            {!hideButton && (
                <BTButton
                    data-testid="add-job-prompt"
                    type="primary"
                    hotkey={hotkey}
                    className="AddNewJobCondensed"
                    block={block}
                    onClick={() => setShowModal(true)}
                    icon={<BdsIconPlus />}
                    aria-label={"New " + newName}
                >
                    {newName}
                </BTButton>
            )}
            <NewJobPromptDisplay
                newName={newName}
                showModal={showModal || Boolean(props.forceModalOpen)}
                setShowModal={setShowModal}
                handleCreateFromScratch={
                    isTemplateMode ? handleCreateTemplateFromScratch : handleCreateJobFromScratch
                }
                handleCreateFromTemplate={handleCreateFromTemplate}
                handleRecommendedTemplatesClick={handleRecommendedTemplatesClick}
                canViewRecommendedTemplates={canViewRecommendedTemplates}
                onModalClose={onModalClose}
            />
        </>
    );
}
